/**
 * Summary content for the inline-prose tool row.
 *
 * Each tool returns a structured :type:`ToolSummary` carrying:
 *
 *   - ``primary`` — the short content the row leads with after the
 *     verb. Almost always a basename, a one-line description, or a
 *     short query. Long commands and absolute paths are NEVER
 *     surfaced here; they live in the expanded body.
 *   - ``primaryMono`` — whether ``primary`` should be monospaced.
 *     Default false; only literal code-voice content (slash
 *     commands, raw queries wrapped in quotes, program names) opts
 *     in.
 *   - ``prose`` — natural-language continuation rendered after
 *     ``primary`` in the same dim color, *without* a leading bullet
 *     ("main.py **lines 12–48**", "tail rome logs
 *     **until session end**"). Use this whenever the metadata
 *     reads as part of the sentence and is unlikely to repeat what
 *     the description already says.
 *   - ``badges`` — already-rendered badge elements (diff numbers,
 *     edit count). Use this for compact tabular metadata; natural
 *     language state belongs in ``primary`` / ``prose``.
 *   - ``primaryVerb`` — overrides the family verb the row leads
 *     with. Used when a tool's own ``description`` starts with an
 *     imperative ("Re-run the rome tests", "Boot the cube sim") so
 *     the row reads ``**Re-run** the rome tests`` instead of the
 *     doubled ``Ran Re-run the rome tests``. The leading verb stays
 *     bold like every other family verb so the column scans the
 *     same way regardless of whether the description supplied the
 *     verb or the family did.
 *
 * Unknown tools route through :func:`fallbackSummary` which picks
 * the most informative single field — never resorts to
 * ``JSON.stringify``.
 *
 * Path metadata (parent directories, absolute prefixes) is
 * intentionally NOT surfaced inline — the row leads with the
 * basename and the full path stays in the expanded body where the
 * user can copy it. Keeps the column scannable when 80% of file
 * operations live under the same project subtree.
 */

import type * as React from 'react';
import type { ToolUseBlock } from '@/lib/agent/types';
import { DiffBadge, EditCountBadge } from './badges';
import {
  asNumber,
  asString,
  basename,
  commandProgram,
  compactShellCommand,
  formatTimeoutCompact,
  humanizeToolName,
  relativizePath,
  truncate,
} from './helpers';

export interface ToolSummary {
  /** Short content the row leads with after the verb. */
  primary: string;
  /** Render ``primary`` in a monospace face (rare — only for code-voice content). */
  primaryMono: boolean;
  /**
   * Natural-language prose continuation rendered after ``primary``
   * in the same dim color, e.g. ``"in the background"`` or
   * ``"lines 12–48"``. No leading bullet — flows as one sentence.
   */
  prose?: string;
  /** Inline badges (diff numbers, edit count). */
  badges?: React.ReactNode;
  /**
   * Overrides the row's bold verb prefix. When the tool supplies a
   * description that already leads with an imperative verb we lift
   * that verb into the bold prefix slot — the row keeps the same
   * "Verb + light prose" shape as every other family without ever
   * doubling ("Ran Re-run …").
   */
  primaryVerb?: string;
  /**
   * Optional Tailwind classes applied to the ``primary`` span. Lets
   * tool-specific summaries colorize their headline (e.g.
   * ``ExitPlanMode`` tints the proposed plan title). Falls through
   * to the row's inherited dim text color when omitted.
   */
  primaryClassName?: string;
}

/**
 * Build the summary for a tool-use block. Caller (the row
 * component) lays it out — this function never emits a full row.
 */
export function buildToolSummary(block: ToolUseBlock, cwd: string | null): ToolSummary {
  const input = block.input ?? {};
  const name = block.name;

  // --- Bash family ----------------------------------------------------------

  if (/^bash$/i.test(name)) {
    return summarizeBash(input);
  }

  if (name === 'Monitor') {
    return summarizeMonitor(input);
  }

  if (name === 'BashOutput') {
    const id = asString(input.bash_id) ?? asString(input.task_id) ?? '';
    const filter = asString(input.filter);
    return {
      primary: truncate(id, 22) || '(no id)',
      primaryMono: true,
      prose: filter ? `matching "${truncate(filter, 40)}"` : undefined,
    };
  }

  if (name === 'KillBash' || name === 'KillShell') {
    const id = asString(input.shell_id) ?? asString(input.bash_id) ?? '';
    return { primary: truncate(id, 22) || '(no id)', primaryMono: true };
  }

  if (name === 'TaskStop') {
    const id = asString(input.task_id) ?? '';
    return { primary: truncate(id, 22) || '(no id)', primaryMono: true };
  }

  // --- File I/O -------------------------------------------------------------

  if (name === 'Read') {
    const rel = relativizePath(asString(input.file_path) ?? '', cwd);
    const offset = asNumber(input.offset);
    const limit = asNumber(input.limit);
    return {
      primary: basename(rel),
      primaryMono: false,
      prose: rangeProse(offset, limit) ?? undefined,
    };
  }

  if (name === 'Write') {
    const rel = relativizePath(asString(input.file_path) ?? '', cwd);
    const content = asString(input.content) ?? '';
    return {
      primary: basename(rel),
      primaryMono: false,
      badges: <DiffBadge added={countLines(content)} />,
    };
  }

  if (name === 'Edit') {
    const rel = relativizePath(asString(input.file_path) ?? '', cwd);
    const oldStr = asString(input.old_string) ?? '';
    const newStr = asString(input.new_string) ?? '';
    return {
      primary: basename(rel),
      primaryMono: false,
      badges: <DiffBadge removed={countLines(oldStr)} added={countLines(newStr)} />,
    };
  }

  if (name === 'MultiEdit') {
    const rel = relativizePath(asString(input.file_path) ?? '', cwd);
    const edits = Array.isArray(input.edits) ? input.edits : [];
    return {
      primary: basename(rel),
      primaryMono: false,
      badges: <EditCountBadge count={edits.length} />,
    };
  }

  if (name === 'NotebookEdit') {
    const rel = relativizePath(asString(input.notebook_path) ?? asString(input.file_path) ?? '', cwd);
    const cellId = asString(input.cell_id);
    return {
      primary: basename(rel),
      primaryMono: false,
      prose: cellId ? `cell ${truncate(cellId, 12)}` : undefined,
    };
  }

  // --- Search ---------------------------------------------------------------

  if (name === 'Grep') {
    const pattern = asString(input.pattern) ?? '';
    const path = asString(input.path);
    const include = asString(input.glob) ?? asString(input.include);
    const rel = path ? relativizePath(path, cwd) : '';
    const proseParts: string[] = [];
    if (rel) proseParts.push(`in ${rel}`);
    if (include) proseParts.push(`matching ${include}`);
    return {
      primary: `"${truncate(pattern, 60)}"`,
      primaryMono: true,
      prose: proseParts.length ? proseParts.join(' ') : undefined,
    };
  }

  if (name === 'Glob') {
    const pattern = asString(input.pattern) ?? '';
    const path = asString(input.path);
    const rel = path ? relativizePath(path, cwd) : '';
    return {
      primary: truncate(pattern, 60),
      primaryMono: true,
      prose: rel ? `in ${rel}` : undefined,
    };
  }

  if (name === 'WebSearch') {
    return {
      primary: truncate(asString(input.query) ?? '', 90),
      primaryMono: false,
    };
  }

  if (name === 'WebFetch') {
    const url = asString(input.url) ?? '';
    return {
      primary: shortUrl(url),
      primaryMono: false,
    };
  }

  // --- Agents and skills ----------------------------------------------------

  if (name === 'Task' || name === 'Agent') {
    const desc = asString(input.description) ?? '';
    return {
      primary: desc ? `subagent ${truncate(desc, 100)}` : 'subagent',
      primaryMono: false,
    };
  }

  if (name === 'Skill' || name === 'SkillTool') {
    const skill = asString(input.skill) ?? asString(input.name) ?? asString(input.path) ?? '';
    return { primary: basename(skill), primaryMono: false };
  }

  if (name === 'SlashCommand') {
    const cmd = asString(input.command) ?? '';
    const normalized = cmd.startsWith('/') ? cmd : `/${cmd}`;
    return { primary: normalized, primaryMono: true };
  }

  if (name === 'ToolSearch') {
    const query = asString(input.query) ?? '';
    return { primary: `"${truncate(query, 60)}"`, primaryMono: false };
  }

  // --- Planning -------------------------------------------------------------

  if (name === 'TodoWrite') {
    const todos = Array.isArray(input.todos) ? (input.todos as Array<Record<string, unknown>>) : [];
    const inProgress = todos.find((t) => asString(t?.status) === 'in_progress');
    const inProgressContent = inProgress ? asString(inProgress.content) : undefined;
    return {
      primary: `${todos.length} item${todos.length === 1 ? '' : 's'}`,
      primaryMono: false,
      prose: inProgressContent ? `· ${truncate(inProgressContent, 90)}` : undefined,
    };
  }

  if (name === 'TaskCreate') {
    // Headline is just the task title; the description lives in the
    // expandable body so the inline row stays a clean one-liner.
    const subject = asString(input.subject) ?? '';
    return { primary: truncate(subject || 'task', 100), primaryMono: false };
  }

  if (name === 'TaskUpdate') {
    // A TaskUpdate input carries only the id + changed fields (no
    // subject), so lead with the id as ``#<id>`` — never a bare ``1``
    // that reads like a title — and trail the changed field names.
    const taskId = asString(input.taskId) ?? '';
    const fields = taskUpdateFields(input);
    return {
      primary: taskId ? `#${taskId}` : 'task',
      primaryMono: true,
      prose: fields.length > 0 ? fields.join(', ') : undefined,
    };
  }

  if (name === 'TaskGet') {
    const taskId = asString(input.taskId) ?? '';
    return { primary: taskId ? `#${taskId}` : 'task', primaryMono: true };
  }

  if (name === 'TaskList') {
    return { primary: 'tasks', primaryMono: false };
  }

  if (name === 'ExitPlanMode') {
    const plan = asString(input.plan) ?? '';
    const firstLine =
      plan
        .split('\n')
        .find((l) => l.trim().length > 0)
        ?.trim() ?? '';
    // Plan titles are typically markdown headers (``# Plan: …``).
    // Strip the leading ``#`` markers + any "Plan:" / "Plan -" prefix
    // so the inline row reads as a clean sentence ("Calculate pi
    // organically") instead of leaking markdown punctuation.
    const stripped = firstLine
      .replace(/^#{1,6}\s+/, '')
      .replace(/^plan\s*[-:—]\s*/i, '')
      .trim();
    return {
      primary: truncate(stripped, 100),
      primaryMono: false,
      // Keep proposed-plan text visually distinct without exposing
      // plan mode as a separate webapp control.
      primaryClassName: 'font-medium text-blue-900 dark:text-blue-200',
    };
  }

  // --- Fallback: smart key picker, never JSON --------------------------------

  // MCP tools land here because they aren't in any built-in family.
  // Show the humanized tool name in the primary slot with the server
  // as a quiet "via …" prose continuation -- "**Called** list kernels
  // via antioch-jupyter" instead of "**Called** input". Falls through
  // to ``fallbackSummary`` only when the model passed enough useful
  // input keys that the standard summary actually has something to
  // say (a path / command / query / description), in which case we
  // still pin the server as prose so the chip identifies the source.
  const humanized = humanizeToolName(name);
  if (humanized.isMcp) {
    const summarized = fallbackSummary(input, cwd);
    return {
      primary: summarized ?? humanized.display,
      primaryMono: false,
      prose: `via ${humanized.server}`,
    };
  }

  return {
    primary: fallbackSummary(input, cwd) ?? 'input',
    primaryMono: false,
  };
}

/**
 * Count the lines in a string without allocating an intermediate array.
 *
 * ``text.split('\n').length`` materializes one array element per line --
 * for a multi-MB ``content`` / ``new_string`` that is a large, short-lived
 * allocation purely to read a count for a diff badge. Counting newline code
 * units in a single pass is equivalent (line count = newline count + 1) and
 * allocation-free. Returns 0 for empty input to match the old guard.
 *
 * :param text: The string to count lines in.
 * :return: The number of lines, or 0 when empty.
 */
function countLines(text: string): number {
  if (text.length === 0) return 0;
  let lines = 1;
  for (let i = 0; i < text.length; i++) {
    if (text.charCodeAt(i) === 10) lines++;
  }
  return lines;
}

function taskUpdateFields(input: Record<string, unknown>): string[] {
  const fields: string[] = [];
  for (const key of ['status', 'subject', 'description', 'activeForm', 'owner']) {
    if (asString(input[key])) fields.push(key);
  }
  const addBlocks = Array.isArray(input.addBlocks) ? input.addBlocks.length : 0;
  if (addBlocks > 0) fields.push(`${addBlocks} ${addBlocks === 1 ? 'block' : 'blocks'}`);
  const addBlockedBy = Array.isArray(input.addBlockedBy) ? input.addBlockedBy.length : 0;
  if (addBlockedBy > 0) fields.push(`${addBlockedBy} ${addBlockedBy === 1 ? 'blocker' : 'blockers'}`);
  return fields;
}

/**
 * Bash row — the headline piece.
 *
 * The model is asked to provide a short ``description``; when it
 * supplies one we lean on it. If the description leads with a
 * recognizable imperative verb ("Re-run the rome tests") we lift
 * that verb into the bold prefix slot via ``primaryVerb`` and show
 * the rest of the sentence as the primary content — the row reads
 * "**Re-run** the rome tests" instead of the doubled
 * "Ran Re-run the rome tests". When the description is just a noun
 * phrase ("livestream sim output") we keep the family "Ran" prefix
 * and show the description after it.
 *
 * Without a description we fall back to a very short compact command
 * preview so the row gives useful context without spilling into a
 * terminal transcript. The full command stays behind the chevron.
 *
 * Background calls get explicit framing by rendering ``"in the
 * background"`` as the lighter prose continuation. The verb stays
 * the original Bash family verb (or the description's leading
 * imperative), so ``Run livestream cube sim`` still reads like the
 * user's requested action.
 */
function summarizeBash(input: Record<string, unknown>): ToolSummary {
  const cmd = asString(input.command) ?? asString(input.cmd) ?? '';
  const description = asString(input.description);
  const isBackground = input.run_in_background === true;

  if (description) {
    const background = splitBackgroundProse(description, isBackground);
    const split = splitLeadingImperative(description);
    if (split) {
      const splitRestBackground = splitBackgroundProse(split.rest, isBackground);
      return {
        primary: truncate(splitRestBackground.primary, 130),
        primaryMono: false,
        prose: splitRestBackground.prose,
        primaryVerb: split.verb,
      };
    }
    return {
      primary: truncate(background.primary, 130),
      primaryMono: false,
      prose: background.prose,
    };
  }
  const fallback = cmd ? compactShellCommand(cmd, 30) : 'command';
  return {
    primary: fallback,
    primaryMono: true,
    prose: isBackground ? 'in the background' : undefined,
  };
}

function splitBackgroundProse(text: string, isBackground: boolean): { primary: string; prose?: string } {
  if (!isBackground) return { primary: text };
  const trimmed = text.trim();
  const withoutBackground = trimmed.replace(/\s+(?:in\s+)?(?:the\s+)?background\s*$/i, '').trim();
  return {
    primary: withoutBackground || trimmed,
    prose: 'in the background',
  };
}

/**
 * Monitor row — like Bash but with an optional ``persistent`` /
 * ``timeout_ms`` budget surfaced as natural-language prose. The
 * default verb is "Monitor" so a row without a description still
 * reads "**Monitor** prime number stream"; when the description
 * starts with its own imperative we lift it into the bold prefix
 * slot the same way Bash does.
 */
function summarizeMonitor(input: Record<string, unknown>): ToolSummary {
  const cmd = asString(input.command) ?? '';
  const description = asString(input.description);
  const persistent = input.persistent === true;
  const timeoutMs = asNumber(input.timeout_ms);

  let prose: string | undefined;
  if (persistent) prose = 'until session end';
  else if (timeoutMs !== undefined) prose = `for up to ${formatTimeoutCompact(timeoutMs)}`;

  if (description) {
    const split = splitLeadingImperative(description);
    if (split) {
      return {
        primary: truncate(split.rest, 110),
        primaryMono: false,
        prose,
        primaryVerb: split.verb,
      };
    }
    return {
      primary: truncate(description, 110),
      primaryMono: false,
      prose,
    };
  }
  return {
    primary: cmd ? compactShellCommand(cmd, 110) : 'process',
    primaryMono: !!cmd,
    prose,
  };
}

/**
 * Detect a leading imperative verb on a tool description so we can
 * lift it into the row's bold prefix slot. Conservative on purpose
 * — only matches a single capitalized word (optionally hyphenated,
 * 2–15 chars) followed by more text. Lower-case openings or
 * non-imperative noun phrases ("livestream sim output") return
 * ``null`` so the row falls back to the family verb. The check is
 * structural (capitalization + length), not a verb whitelist, so
 * new descriptions don't have to round-trip through this file.
 */
function splitLeadingImperative(text: string): { verb: string; rest: string } | null {
  const trimmed = text.trim();
  const m = trimmed.match(/^([A-Z][A-Za-z-]{1,14})\s+(.+)$/);
  if (!m) return null;
  return { verb: m[1], rest: m[2] };
}

/**
 * Convert SDK subagent ids (``generalPurpose``, ``browser-use``,
 * ``local_agent``) into compact badge text.
 */
export function formatSubagentType(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

/**
 * Best-effort one-liner for tools we don't have a hand-rolled
 * preview for. Pulls the most informative single field — never
 * ``JSON.stringify``s the input — so unknown tools still produce a
 * legible row instead of a wall of braces.
 */
function fallbackSummary(input: Record<string, unknown>, cwd: string | null): string | undefined {
  const description = asString(input.description);
  if (description) return truncate(description, 110);
  const command = asString(input.command) ?? asString(input.cmd);
  if (command) {
    const program = commandProgram(command);
    return program ? `${program} …` : truncate(command, 60);
  }
  const path = asString(input.file_path) ?? asString(input.notebook_path) ?? asString(input.path);
  if (path) return basename(relativizePath(path, cwd));
  const query = asString(input.query) ?? asString(input.prompt) ?? asString(input.pattern);
  if (query) return truncate(query, 100);
  const url = asString(input.url);
  if (url) return shortUrl(url);

  const keys = Object.keys(input);
  if (keys.length === 0) return undefined;
  return keys.slice(0, 6).join(', ');
}

/**
 * Build the ``"lines 12–48"`` / ``"from line 12"`` natural-language
 * trail for a Read with a range. Returns ``null`` when the read
 * covers the whole file (no range info).
 */
function rangeProse(offset: number | undefined, limit: number | undefined): string | null {
  if (offset === undefined && limit === undefined) return null;
  const start = offset ?? 1;
  if (limit === undefined) return `from line ${start}`;
  const end = start + Math.max(limit - 1, 0);
  return `lines ${start}–${end}`;
}

/**
 * Compact a URL for the inline row — drop the scheme, drop the
 * trailing slash, truncate the path. The full URL stays in the
 * expanded body. Returns the original on parse failure so we never
 * blank out the row.
 */
function shortUrl(url: string): string {
  if (!url) return '';
  try {
    const u = new URL(url);
    const path = u.pathname.replace(/\/+$/, '');
    const tail = `${u.hostname}${path}`;
    return truncate(tail, 70);
  } catch {
    return truncate(url.replace(/^https?:\/\//, ''), 70);
  }
}
